import { Fragment } from "react";

import { css } from "@compiled/react";
// eslint-disable-next-line no-restricted-imports
import styled from "@emotion/styled";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage } from "react-intl";

import Lozenge from "@atlaskit/lozenge";
import Tooltip, { TooltipPrimitive } from "@atlaskit/tooltip";
import { getColor } from "@atlassiansox/wpl-utils";

import {
    FormattedPriceCalculationResponse,
    PriceFinderMessages
} from "../constants";

const userTierContainerStyles = css({
    display: "flex",
    justifyContent: "center",
    cursor: "pointer"
});

const userTierMessageStyles = css({
    borderRadius: "4px",
    fontSize: "11px",
    lineHeight: "16px",
    textTransform: "uppercase",
    fontStyle: "normal",
    fontWeight: "normal",
    fontFamily: "Charlie Text"
});

const userTierDetailsStyles = css({
    color: getColor("n0"),
    fontSize: "20px",
    lineHeight: "24px",
    fontFamily: "Charlie Display",
    letterSpacing: "0.3px",
    marginBlockEnd: "20px",
    marginBlockStart: "20px",
    marginInlineStart: "40px",
    paddingInlineEnd: "32px"
});

const userTierAndSeatsLabelStyles = css({
    color: getColor("n0"),
    fontSize: "12px",
    lineHeight: "21px",
    height: "21px",
    fontFamily: "Charlie Display",
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginInlineStart: "30px"
});

const userTierFormattedLimitsStyles = css({
    color: getColor("n0"),
    fontSize: "16px",
    lineHeight: "24px",
    height: "24px",
    fontFamily: "Charlie Text",
    textTransform: "uppercase",
    marginInlineStart: "30px",
    marginBlockEnd: "20px"
});

const marginContainerStyles = css({
    background: getColor("n0"),
    height: "1px",
    marginBlockEnd: "20px",
    marginInlineStart: "30px",
    width: "176px"
});

const toolTipContainerStyles = css({
    display: "flex",
    alignItems: "center",
    gap: "12px"
});

//TODO: Remove when able to use @compiled/react -- fix styling
const TooltipContentContainer = styled(TooltipPrimitive)`
    background: ${getColor("n900")};
    border-radius: 12px;
    box-shadow: 0px 3px 5px rgba(9, 30, 66, 0.2),
        0px 0px 1px rgba(9, 30, 66, 0.31);
    max-width: 240px;
    padding: 3px 3px 3px 1px;
`;

export type UserTierProps = {
    priceCalc: FormattedPriceCalculationResponse;
    messages: PriceFinderMessages;
};

export const UserTier = ({ priceCalc, messages }: UserTierProps) => {
    const { formattedBreakdown } = priceCalc;

    const fmtBreakdownTier = formattedBreakdown[formattedBreakdown.length - 1];
    if (!fmtBreakdownTier) {
        throw new Error("No breakdown provided for user tier.");
    }

    const {
        formattedUnitLimitLower,
        formattedUnitLimitUpper,
        formattedUnitsInTier,
        formattedUnitsAvailableInTier
    } = fmtBreakdownTier;

    return (
        <div css={userTierContainerStyles}>
            <Tooltip
                delay={300}
                component={TooltipContentContainer}
                content={
                    <Fragment>
                        <div css={userTierDetailsStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.UserTier.Details"
                                defaultMessage="Details for this tier"
                            />
                        </div>
                        <div css={marginContainerStyles} />
                        <div css={userTierAndSeatsLabelStyles}>tier</div>
                        <div css={userTierFormattedLimitsStyles}>
                            {formattedUnitLimitLower} -{" "}
                            {formattedUnitLimitUpper}
                        </div>

                        <div css={userTierAndSeatsLabelStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.UserTier.FilledSeats"
                                defaultMessage="Filled seats"
                            />
                        </div>
                        <div css={userTierFormattedLimitsStyles}>
                            {formattedUnitsInTier}
                        </div>
                        <div css={userTierAndSeatsLabelStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.UserTier.AvailableSeats"
                                defaultMessage="Available seats"
                            />
                        </div>
                        <div css={userTierFormattedLimitsStyles}>
                            {formattedUnitsAvailableInTier}
                        </div>
                    </Fragment>
                }
                position="bottom-end"
            >
                <div css={toolTipContainerStyles}>
                    <Lozenge
                        style={{
                            backgroundColor: "#172B4D",
                            color: getColor("n0")
                        }}
                    >
                        <div css={userTierMessageStyles}>
                            <FormattedMessage
                                {...messages.cardUnitCountTierRange}
                                values={{
                                    ...DEFAULT_VALUES,
                                    lowerLimit: formattedUnitLimitLower,
                                    upperLimit: formattedUnitLimitUpper
                                }}
                            />
                        </div>
                    </Lozenge>
                    <InfoIcon />
                </div>
            </Tooltip>
        </div>
    );
};

const InfoIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        role="presentation"
        tabIndex={0}
    >
        <g fillRule="evenodd">
            <circle fill="#172b4d" cx="12" cy="12" r="10" />
            <circle fill="#fff" cx="12" cy="18" r="1" />
            <path
                d="M15.89 9.05a3.975 3.975 0 00-2.957-2.942C10.321 5.514 8.017 7.446 8 9.95l.005.147a.992.992 0 00.982.904c.552 0 1-.447 1.002-.998a2.004 2.004 0 014.007-.002c0 1.102-.898 2-2.003 2H12a1 1 0 00-1 .987v2.014a1.001 1.001 0 002.004 0v-.782c0-.217.145-.399.35-.472A3.99 3.99 0 0015.89 9.05"
                fill="#fff"
            />
        </g>
    </svg>
);
